const React = require('react');
const { createElement: h, useState, useEffect, useCallback } = React;
const { getApiOrigin } = require('../lib/apiClient');
const oauthApps = require('../lib/oauthApps');
const ConfirmDialog = require('./ConfirmDialog');
require('./DeveloperAppsPage.css');

// ---------------------------------------------------------------------------
// Developer page: register a third-party app that other Alleflow users can
// sign in to. The developer picks the permission scopes their app may ask for;
// the consent screen can never request more than what is ticked here.
// ---------------------------------------------------------------------------

function formatDate(value) {
  if (!value) return '-';
  var date = new Date(value);
  if (!Number.isFinite(date.getTime())) return '-';
  var months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return months[date.getMonth()] + ' ' + date.getDate() + ', ' + date.getFullYear();
}

function apiOrigin() {
  return getApiOrigin() || window.location.origin;
}

// /v1 sits at the API root, the rest of the API lives under /api, and the
// consent screen is a page on the WEB app — which in production is a different
// domain from the API. Keep the three apart.
function apiBase() {
  return apiOrigin() + '/api';
}

function siteOrigin() {
  return window.location.origin;
}

// Blank form state, reused by "create" and by resetting after a save.
function emptyForm() {
  return {
    name: '',
    description: '',
    homepageUrl: '',
    redirectUris: '',
    scopes: ['openid', 'profile', 'workflows:read']
  };
}

/* -- scope checkboxes ---------------------------------------------------- */

function ScopePicker(props) {
  var available = props.available || [];
  var selected = props.selected || [];

  function toggle(scope) {
    var next = selected.indexOf(scope) === -1
      ? selected.concat([scope])
      : selected.filter(function(s) { return s !== scope; });
    props.onChange(next);
  }

  return h('div', { className: 'dev-scopes' },
    available.map(function(item) {
      var checked = selected.indexOf(item.scope) !== -1;
      return h('label', {
        key: item.scope,
        className: 'dev-scope' + (checked ? ' dev-scope--on' : '')
      },
        h('input', {
          type: 'checkbox',
          className: 'dev-scope__box',
          checked: checked,
          onChange: function() { toggle(item.scope); }
        }),
        h('span', { className: 'dev-scope__text' },
          h('code', { className: 'dev-scope__name' }, item.scope),
          h('span', { className: 'dev-scope__label' }, item.label),
          h('span', { className: 'dev-scope__desc' }, item.description)
        )
      );
    })
  );
}

/* -- main page ----------------------------------------------------------- */

function DeveloperAppsPage() {
  var [apps, setApps] = useState([]);
  var [availableScopes, setAvailableScopes] = useState([]);
  var [loading, setLoading] = useState(true);
  var [form, setForm] = useState(emptyForm());
  var [editingId, setEditingId] = useState('');
  var [saving, setSaving] = useState(false);
  var [error, setError] = useState('');
  var [newSecret, setNewSecret] = useState(null);
  var [copied, setCopied] = useState('');
  var [deleteTarget, setDeleteTarget] = useState(null);
  var [formOpen, setFormOpen] = useState(false);

  var load = useCallback(function() {
    setLoading(true);
    Promise.all([oauthApps.listApps(), oauthApps.listScopes()])
      .then(function(results) {
        setApps(results[0] || []);
        setAvailableScopes(results[1] || []);
      })
      .catch(function(err) {
        setError((err && err.message) || 'Failed to load your apps.');
      })
      .finally(function() { setLoading(false); });
  }, []);

  useEffect(function() { load(); }, [load]);

  function updateForm(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
  }

  function resetForm() {
    setForm(emptyForm());
    setEditingId('');
    setFormOpen(false);
  }

  // The textarea holds one redirect URI per line — split and clean here so the
  // API always receives a proper array.
  function parseRedirectUris(raw) {
    return String(raw || '')
      .split('\n')
      .map(function(line) { return line.trim(); })
      .filter(function(line) { return !!line; });
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (saving) return;

    var payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      homepageUrl: form.homepageUrl.trim(),
      redirectUris: parseRedirectUris(form.redirectUris),
      scopes: form.scopes
    };

    if (!payload.name) { setError('App name is required.'); return; }
    if (!payload.redirectUris.length) { setError('At least one redirect URI is required.'); return; }
    if (!payload.scopes.length) { setError('Select at least one permission scope.'); return; }

    setSaving(true);
    setError('');
    setNewSecret(null);

    var action = editingId
      ? oauthApps.updateApp(editingId, payload)
      : oauthApps.createApp(payload);

    action
      .then(function(app) {
        // A newly created app returns its secret exactly once.
        if (app && app.clientSecret) setNewSecret(app);
        resetForm();
        load();
      })
      .catch(function(err) {
        setError((err && err.message) || 'Failed to save the app.');
      })
      .finally(function() { setSaving(false); });
  }

  function startEdit(app) {
    setForm({
      name: app.name || '',
      description: app.description || '',
      homepageUrl: app.homepageUrl || '',
      redirectUris: (app.redirectUris || []).join('\n'),
      scopes: app.scopes || []
    });
    setEditingId(app.id);
    setFormOpen(true);
    setNewSecret(null);
    setError('');
  }

  function handleRegenerate(app) {
    setError('');
    oauthApps.regenerateSecret(app.id)
      .then(function(updated) {
        setNewSecret(updated);
        load();
      })
      .catch(function(err) {
        setError((err && err.message) || 'Failed to regenerate the secret.');
      });
  }

  function confirmDelete() {
    var target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    oauthApps.deleteApp(target.id)
      .then(function() {
        if (newSecret && newSecret.id === target.id) setNewSecret(null);
        load();
      })
      .catch(function(err) {
        setError((err && err.message) || 'Failed to delete the app.');
      });
  }

  function copy(text, marker) {
    navigator.clipboard.writeText(text).then(function() {
      setCopied(marker);
      setTimeout(function() { setCopied(''); }, 2000);
    }).catch(function() {});
  }

  return h('div', { className: 'dev-content' },
    h('header', { className: 'dev-hero' },
      h('p', { className: 'dev-hero__eyebrow' }, 'Developer'),
      h('h1', { className: 'dev-hero__title' }, 'OAuth Apps'),
      h('p', { className: 'dev-hero__copy' },
        'Register an app so any Alleflow user can sign in to it and let it work with their workflows. You choose which permissions your app is allowed to request.')
    ),

    error ? h('div', { className: 'dev-error' }, error) : null,

    newSecret
      ? h('div', { className: 'dev-secret-banner' },
          h('p', { className: 'dev-secret-banner__label' },
            'Copy your client secret now — it cannot be shown again.'),
          h('div', { className: 'dev-secret-banner__row' },
            h('span', { className: 'dev-secret-banner__key' }, 'Client ID'),
            h('code', { className: 'dev-secret-banner__value' }, newSecret.clientId),
            h('button', {
              type: 'button',
              className: 'dev-copy-btn',
              onClick: function() { copy(newSecret.clientId, 'id'); }
            }, copied === 'id' ? 'Copied!' : 'Copy')
          ),
          h('div', { className: 'dev-secret-banner__row' },
            h('span', { className: 'dev-secret-banner__key' }, 'Client secret'),
            h('code', { className: 'dev-secret-banner__value' }, newSecret.clientSecret),
            h('button', {
              type: 'button',
              className: 'dev-copy-btn',
              onClick: function() { copy(newSecret.clientSecret, 'secret'); }
            }, copied === 'secret' ? 'Copied!' : 'Copy')
          )
        )
      : null,

    h('div', { className: 'dev-section' },
      h('div', { className: 'dev-section__head' },
        h('h2', { className: 'dev-section__title' }, editingId ? 'Edit app' : 'Register an app'),
        !formOpen
          ? h('button', {
              type: 'button',
              className: 'dev-primary-btn',
              onClick: function() { setFormOpen(true); }
            }, 'New app')
          : null
      ),

      formOpen
        ? h('form', { className: 'dev-form', onSubmit: handleSubmit },
            h('label', { className: 'dev-field' },
              h('span', { className: 'dev-field__label' }, 'App name'),
              h('input', {
                className: 'dev-input',
                type: 'text',
                placeholder: 'My Integration',
                value: form.name,
                onChange: function(e) { updateForm('name', e.target.value); }
              })
            ),
            h('label', { className: 'dev-field' },
              h('span', { className: 'dev-field__label' }, 'Description'),
              h('span', { className: 'dev-field__hint' }, 'Shown to users on the consent screen.'),
              h('input', {
                className: 'dev-input',
                type: 'text',
                placeholder: 'Syncs Alleflow workflows into our dashboard',
                value: form.description,
                onChange: function(e) { updateForm('description', e.target.value); }
              })
            ),
            h('label', { className: 'dev-field' },
              h('span', { className: 'dev-field__label' }, 'Homepage URL'),
              h('input', {
                className: 'dev-input',
                type: 'text',
                placeholder: 'https://example.com',
                value: form.homepageUrl,
                onChange: function(e) { updateForm('homepageUrl', e.target.value); }
              })
            ),
            h('label', { className: 'dev-field' },
              h('span', { className: 'dev-field__label' }, 'Redirect URIs'),
              h('span', { className: 'dev-field__hint' },
                'One per line. Must match exactly at authorize time. https only, except http://localhost.'),
              h('textarea', {
                className: 'dev-textarea',
                rows: 3,
                placeholder: 'https://example.com/oauth/callback',
                value: form.redirectUris,
                onChange: function(e) { updateForm('redirectUris', e.target.value); }
              })
            ),
            h('div', { className: 'dev-field' },
              h('span', { className: 'dev-field__label' }, 'Permission scopes'),
              h('span', { className: 'dev-field__hint' },
                'Your app can never request more than what you tick here.'),
              h(ScopePicker, {
                available: availableScopes,
                selected: form.scopes,
                onChange: function(next) { updateForm('scopes', next); }
              })
            ),
            h('div', { className: 'dev-form__actions' },
              h('button', {
                type: 'submit',
                className: 'dev-primary-btn',
                disabled: saving
              }, saving ? 'Saving...' : (editingId ? 'Save changes' : 'Create app')),
              h('button', {
                type: 'button',
                className: 'dev-ghost-btn',
                onClick: resetForm
              }, 'Cancel')
            )
          )
        : null
    ),

    h('div', { className: 'dev-section' },
      h('h2', { className: 'dev-section__title' }, 'Your apps'),
      loading
        ? h('p', { className: 'dev-loading' }, 'Loading...')
        : apps.length === 0
          ? h('p', { className: 'dev-empty' }, 'No OAuth apps yet.')
          : h('div', { className: 'dev-app-list' },
              apps.map(function(app) {
                return h('div', { className: 'dev-app', key: app.id },
                  h('div', { className: 'dev-app__head' },
                    h('div', null,
                      h('h3', { className: 'dev-app__name' }, app.name),
                      app.description
                        ? h('p', { className: 'dev-app__desc' }, app.description)
                        : null
                    ),
                    h('div', { className: 'dev-app__actions' },
                      h('button', {
                        type: 'button',
                        className: 'dev-ghost-btn',
                        onClick: function() { startEdit(app); }
                      }, 'Edit'),
                      h('button', {
                        type: 'button',
                        className: 'dev-ghost-btn',
                        onClick: function() { handleRegenerate(app); }
                      }, 'New secret'),
                      h('button', {
                        type: 'button',
                        className: 'dev-danger-btn',
                        onClick: function() { setDeleteTarget(app); }
                      }, 'Delete')
                    )
                  ),
                  h('div', { className: 'dev-app__row' },
                    h('span', { className: 'dev-app__key' }, 'Client ID'),
                    h('code', { className: 'dev-app__value' }, app.clientId),
                    h('button', {
                      type: 'button',
                      className: 'dev-copy-btn',
                      onClick: function() { copy(app.clientId, app.id); }
                    }, copied === app.id ? 'Copied!' : 'Copy')
                  ),
                  h('div', { className: 'dev-app__row' },
                    h('span', { className: 'dev-app__key' }, 'Client secret'),
                    h('code', { className: 'dev-app__value dev-app__value--muted' },
                      (app.clientSecretPrefix || 'afs_') + '...')
                  ),
                  h('div', { className: 'dev-app__row' },
                    h('span', { className: 'dev-app__key' }, 'Redirect URIs'),
                    h('div', { className: 'dev-app__uris' },
                      (app.redirectUris || []).map(function(uri, i) {
                        return h('code', { key: i, className: 'dev-app__uri' }, uri);
                      })
                    )
                  ),
                  h('div', { className: 'dev-app__row' },
                    h('span', { className: 'dev-app__key' }, 'Scopes'),
                    h('div', { className: 'dev-app__chips' },
                      (app.scopes || []).map(function(scope) {
                        return h('span', { key: scope, className: 'dev-chip' }, scope);
                      })
                    )
                  ),
                  h('div', { className: 'dev-app__meta' },
                    'Created ' + formatDate(app.createdAt) +
                    ' · Last used ' + (app.lastUsedAt ? formatDate(app.lastUsedAt) : 'Never')
                  )
                );
              })
            )
    ),

    deleteTarget
      ? h(ConfirmDialog, {
          title: 'Delete this app?',
          message: 'Deleting "' + (deleteTarget.name || 'this app') +
            '" signs out every user who approved it and immediately invalidates its tokens. This cannot be undone.',
          confirmLabel: 'Delete app',
          cancelLabel: 'Cancel',
          danger: true,
          onConfirm: confirmDelete,
          onCancel: function() { setDeleteTarget(null); }
        })
      : null,

    h('div', { className: 'dev-section' },
      h('h2', { className: 'dev-section__title' }, 'Connecting a user'),
      h('p', { className: 'dev-docs__text' },
        'Send the user to the authorize URL. After they approve, exchange the returned code for an access token.'),
      h('pre', { className: 'dev-code-block' },
        h('code', null, [
          '# 1. Send the user here',
          siteOrigin() + '/oauth/authorize' +
            '?client_id=YOUR_CLIENT_ID',
          '  &redirect_uri=https://example.com/oauth/callback',
          '  &response_type=code',
          '  &scope=openid%20profile%20workflows:read',
          '  &state=RANDOM_STRING',
          '',
          '# 2. Trade the code for a token',
          'curl -X POST ' + apiBase() + '/oauth/token \\',
          '  -d grant_type=authorization_code \\',
          '  -d code=THE_CODE \\',
          '  -d client_id=YOUR_CLIENT_ID \\',
          '  -d client_secret=YOUR_CLIENT_SECRET \\',
          '  -d redirect_uri=https://example.com/oauth/callback',
          '',
          '# 3. Call the API as that user',
          'curl -H "Authorization: Bearer afa_..." ' + apiOrigin() + '/v1/workflows'
        ].join('\n'))
      ),
      h('p', { className: 'dev-docs__text' },
        'Full reference, including PKCE and refresh tokens, is on the API Docs page.')
    )
  );
}

module.exports = DeveloperAppsPage;
